type ProjectEditorsResponse = SuccessResponseBody<'/project/{projectId}/editors'>

export type EditRequestStatus = 'none' | 'pending_request' | 'approved' | 'denied'

/**
 * Decides what the current user should be notified about project management based on a transition
 * from the previous editors snapshot (`prev`) to the current one (`curr`).
 *
 * Semantics (per-user perspective):
 * - 'pending_request' → You are the **active editor** and there is someone pending.
 *   Fires on each refresh while a request is pending.
 * - 'approved'        → You were pending and now became the active editor.
 * - 'denied'          → You were pending, but now you're no longer pending and not active.
 * - 'none'            → No actionable change for you.
 */
export function evaluateEditRequestStatus(prev: ProjectEditorsResponse | undefined, curr: ProjectEditorsResponse, userId: string): EditRequestStatus {
  const pendingEditorId = curr.pendingEditor?.id ?? null
  const activeEditorId = curr.activeEditor.id
  const prevPendingEditorId = prev?.pendingEditor?.id ?? null
  const prevActiveEditorId = prev?.activeEditor?.id ?? null

  if (activeEditorId === userId && pendingEditorId !== null) {
    return 'pending_request'
  }

  const wasAwaiting = prevPendingEditorId === userId || pendingEditorId === userId
  if (!wasAwaiting) {
    return 'none'
  }

  const becameActive = activeEditorId === userId
  if (becameActive && prevActiveEditorId !== userId) {
    return 'approved'
  }

  const hadBeenPending = prevPendingEditorId === userId
  const notPendingNow = pendingEditorId !== userId
  if (hadBeenPending && notPendingNow) {
    return 'denied'
  }

  return 'none'
}
